import { useState } from "react";
import {
  Home,
  Wallet,
  Target,
  Plus,
  CalendarDays,
  Sparkles,
  MoreHorizontal,
  Lightbulb,
  Bug,
  Camera,
  CircleDollarSign,
  X,
  LineChart,
  RefreshCw,
  FileText,
  Users,
  Map as MapIcon,
  Settings as SettingsIcon,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { cn } from "@/lib/utils";
import { useLocation, useNavigate } from "react-router-dom";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
  DrawerDescription,
} from "@/components/ui/drawer";
import { FeedbackModal } from "@/components/feedback/FeedbackModal";
import { useFamilyScope } from "@/contexts/FamilyScopeContext";
import { ScopeMenuItems } from "@/components/family/ScopeMenuItems";
import type { FamilyNavKey } from "@/lib/familyNavigation";
import type { TabType } from "@/types/navigation";

interface MobileNavProps {
  activeTab: TabType;
  onTabChange: (tab: TabType) => void;
  onAddExpense: () => void;
  onScanReceipt?: () => void;
  onAddToGoal?: () => void;
  resumableDraft?: { id: string; thumbnail?: string; createdAt: number } | null;
  onResumeScanDraft?: () => void;
  onDismissScanDraft?: () => void;
  /** Capability filter from the app shell. Defaults to allowing everything. */
  can?: (key: FamilyNavKey) => boolean;
}

type SlotKind = "tab" | "route";
interface NavSlot {
  key: string;
  label: string;
  icon: typeof Home;
  kind: SlotKind;
  tab?: TabType;
  route?: string;
}

export function MobileNav({
  activeTab,
  onTabChange,
  onAddExpense,
  onScanReceipt,
  onAddToGoal,
  resumableDraft,
  onResumeScanDraft,
  onDismissScanDraft,
  can,
}: MobileNavProps) {
  // Which entries the active view may show. Personal scope allows everything;
  // a family scope shows only what the member's role grants.
  const { t } = useTranslation("nav");
  const allow = can ?? (() => true);
  const { availableFamilies } = useFamilyScope();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const [feedbackType, setFeedbackType] = useState<"feature" | "bug">("feature");
  const [fabDrawerOpen, setFabDrawerOpen] = useState(false);

  const allLeftSlots: NavSlot[] = [
    { key: "home", label: t("items.home"), icon: Home, kind: "tab", tab: "home" },
    { key: "budget", label: t("items.budget"), icon: Wallet, kind: "tab", tab: "budget" },
    { key: "goals", label: t("items.goals"), icon: Target, kind: "tab", tab: "goals" },
  ];
  const leftSlots = allLeftSlots.filter((slot) => allow(slot.key as FamilyNavKey));

  const allRightSlots: NavSlot[] = [
    { key: "insights", label: t("items.insights"), icon: LineChart, kind: "tab", tab: "insights" },
    { key: "recurring", label: t("items.recurring"), icon: RefreshCw, kind: "route", route: "/upcoming" },
  ];
  const rightSlots = allRightSlots.filter((slot) => allow(slot.key as FamilyNavKey));

  const isSlotActive = (slot: NavSlot) => {
    if (slot.kind === "tab" && slot.tab) {
      if (slot.tab === "home") return activeTab === "home" && pathname === "/";
      if (slot.tab === "budget") return pathname.startsWith("/budget");
      if (slot.tab === "goals") return pathname.startsWith("/goals");
      if (slot.tab === "calendar") return pathname.startsWith("/calendar");
      if (slot.tab === "insights") return pathname.startsWith("/insights") || activeTab === "insights";
    }
    if (slot.kind === "route" && slot.route) return pathname.startsWith(slot.route);
    return false;
  };

  const handleSlot = (slot: NavSlot) => {
    if (slot.kind === "tab" && slot.tab) return onTabChange(slot.tab);
    if (slot.kind === "route" && slot.route) return navigate(slot.route);
  };

  const renderSlot = (slot: NavSlot) => {
    const Icon = slot.icon;
    const active = isSlotActive(slot);
    return (
      <button
        key={slot.key}
        onClick={() => handleSlot(slot)}
        className="flex flex-col items-center gap-1 flex-1 group min-w-0 py-1"
        aria-label={slot.label}
      >
        <Icon
          className={cn(
            "h-5 w-5 transition-colors",
            active ? "text-primary" : "text-foreground/60 group-hover:text-foreground",
          )}
          strokeWidth={active ? 2 : 1.5}
        />
        <span
          className={cn(
            "text-[10px] font-medium transition-colors leading-none",
            active ? "text-primary" : "text-foreground/60 group-hover:text-foreground",
          )}
        >
          {slot.label}
        </span>
        <span
          className={cn(
            "h-1 w-1 rounded-full transition-colors -mt-0.5",
            active ? "bg-primary" : "bg-transparent",
          )}
        />
      </button>
    );
  };

  return (
    <>
      {/* Floating Obsidian Pill */}
      <nav
        className="lg:hidden fixed start-0 end-0 z-40 px-4 pointer-events-none"
        style={{ bottom: "calc(env(safe-area-inset-bottom, 0px) + 12px)" }}
      >
        <div className="mx-auto max-w-[480px] md:max-w-[560px] pointer-events-auto">
          <div className="relative flex items-center justify-between rounded-[32px] bg-card/95 backdrop-blur-xl border border-border/40 shadow-2xl px-2 py-2.5">
            {/* Left group */}
            <div className="flex flex-1 items-center">{leftSlots.map(renderSlot)}</div>

            {/* Center FAB */}
            <div className="relative -top-6 px-2 shrink-0">
              <button
                onClick={() => setFabDrawerOpen(true)}
                aria-label={t("mobile.quickActions")}
                className="w-14 h-14 rounded-full bg-primary flex items-center justify-center shadow-[0_8px_24px_hsl(var(--primary)/0.35)] ring-4 ring-background active:scale-95 hover:scale-105 transition-transform"
              >
                <Plus className="h-7 w-7 text-primary-foreground" strokeWidth={2.5} />
              </button>
            </div>

            {/* Right group */}
            <div className="flex flex-1 items-center">
              {rightSlots.map(renderSlot)}

              {/* More */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    className="flex flex-col items-center gap-1 flex-1 group min-w-0 py-1"
                    aria-label={t("mobile.more")}
                  >
                    <MoreHorizontal className="h-5 w-5 text-foreground/60 group-hover:text-foreground" strokeWidth={1.5} />
                    <span className="text-[10px] font-medium text-foreground/60 group-hover:text-foreground leading-none">
                      {t("mobile.more")}
                    </span>
                    <span className="h-1 w-1 rounded-full bg-transparent -mt-0.5" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" side="top" className="w-52 mb-2">
                  {availableFamilies.length > 0 && (
                    <>
                      <ScopeMenuItems />
                      <DropdownMenuSeparator />
                    </>
                  )}
                  {allow("calendar") && (
                    <DropdownMenuItem onClick={() => onTabChange("calendar")}>
                      <CalendarDays className="h-4 w-4 me-2" />
                      {t("items.calendar")}
                    </DropdownMenuItem>
                  )}
                  {allow("journey") && (
                    <DropdownMenuItem onClick={() => navigate("/journey")}>
                      <Sparkles className="h-4 w-4 me-2" />
                      {t("items.fennie")}
                    </DropdownMenuItem>
                  )}
                  {allow("family") && (
                    <DropdownMenuItem onClick={() => navigate("/family")}>
                      <Users className="h-4 w-4 me-2" />
                      {t("items.family")}
                    </DropdownMenuItem>
                  )}
                  {allow("documents") && (
                    <DropdownMenuItem onClick={() => navigate("/documents")}>
                      <FileText className="h-4 w-4 me-2" />
                      {t("items.documents")}
                    </DropdownMenuItem>
                  )}
                  {allow("roadmap") && (
                    <DropdownMenuItem onClick={() => navigate("/roadmap")}>
                      <MapIcon className="h-4 w-4 me-2" />
                      {t("items.roadmap")}
                    </DropdownMenuItem>
                  )}
                  {allow("settings") && (
                    <DropdownMenuItem onClick={() => onTabChange("settings")}>
                      <SettingsIcon className="h-4 w-4 me-2" />
                      {t("items.settings")}
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    onClick={() => {
                      setFeedbackType("feature");
                      setFeedbackOpen(true);
                    }}
                  >
                    <Lightbulb className="h-4 w-4 me-2 text-amber-500" />
                    {t("mobile.requestFeature")}
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={() => {
                      setFeedbackType("bug");
                      setFeedbackOpen(true);
                    }}
                  >
                    <Bug className="h-4 w-4 me-2 text-destructive" />
                    {t("mobile.reportBug")}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </div>
      </nav>

      {/* FAB Action Sheet */}
      <Drawer open={fabDrawerOpen} onOpenChange={setFabDrawerOpen}>
        <DrawerContent className="px-4 pb-8">
          <DrawerHeader className="text-center">
            <DrawerTitle>{t("mobile.sheetTitle")}</DrawerTitle>
            <DrawerDescription>{t("mobile.sheetDescription")}</DrawerDescription>
          </DrawerHeader>
          <div className="grid gap-3 px-4">
            {resumableDraft && (
              <div className="flex items-center gap-3 p-3 rounded-xl border border-primary/30 bg-primary/5">
                {resumableDraft.thumbnail ? (
                  <img
                    src={resumableDraft.thumbnail}
                    alt={t("mobile.scannedReceiptAlt")}
                    className="h-12 w-12 shrink-0 rounded-md object-cover"
                  />
                ) : (
                  <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-md bg-primary/10">
                    <Sparkles className="h-5 w-5 text-primary" />
                  </div>
                )}
                <div className="min-w-0 flex-1 text-start">
                  <p className="text-sm font-medium">{t("mobile.unfinishedReceipt")}</p>
                  <p className="text-xs text-muted-foreground">
                    {(() => {
                      const m = Math.max(1, Math.round((Date.now() - resumableDraft.createdAt) / 60000));
                      return m < 60
                        ? t("mobile.scannedAgoMinutes", { count: m })
                        : m < 1440
                          ? t("mobile.scannedAgoHours", { count: Math.round(m / 60) })
                          : t("mobile.scannedAgoDays", { count: Math.round(m / 1440) });
                    })()}
                  </p>
                </div>
                <div className="flex items-center gap-1.5">
                  <button
                    onClick={() => {
                      setFabDrawerOpen(false);
                      onResumeScanDraft?.();
                    }}
                    className="px-3 py-1.5 rounded-md bg-primary text-primary-foreground text-xs font-medium"
                  >
                    {t("mobile.resume")}
                  </button>
                  <button
                    onClick={() => onDismissScanDraft?.()}
                    aria-label={t("mobile.dismiss")}
                    className="p-1.5 rounded-full hover:bg-muted text-muted-foreground"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              </div>
            )}
            {allow("add") && (
            <button
              onClick={() => {
                setFabDrawerOpen(false);
                onAddExpense();
              }}
              className="flex items-center gap-4 p-4 rounded-xl bg-muted/30 hover:bg-muted/50 transition-colors text-start"
            >
              <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                <CircleDollarSign className="h-5 w-5 text-primary" />
              </div>
              <div>
                <div className="font-medium text-foreground">{t("mobile.addExpense")}</div>
                <div className="text-xs text-muted-foreground">{t("mobile.addExpenseDescription")}</div>
              </div>
            </button>
            )}

            {allow("scanner") && (
            <button
              onClick={() => {
                setFabDrawerOpen(false);
                onScanReceipt?.();
              }}
              className="flex items-center gap-4 p-4 rounded-xl bg-muted/30 hover:bg-muted/50 transition-colors text-start"
            >
              <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                <Camera className="h-5 w-5 text-primary" />
              </div>
              <div>
                <div className="font-medium text-foreground">{t("mobile.scanReceipt")}</div>
                <div className="text-xs text-muted-foreground">{t("mobile.scanReceiptDescription")}</div>
              </div>
            </button>
            )}

            {allow("goal_contribution") && (
            <button
              onClick={() => {
                setFabDrawerOpen(false);
                onAddToGoal?.();
              }}
              className="flex items-center gap-4 p-4 rounded-xl bg-muted/30 hover:bg-muted/50 transition-colors text-start"
            >
              <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                <Target className="h-5 w-5 text-primary" />
              </div>
              <div>
                <div className="font-medium text-foreground">{t("mobile.addToGoal")}</div>
                <div className="text-xs text-muted-foreground">{t("mobile.addToGoalDescription")}</div>
              </div>
            </button>
            )}
          </div>
        </DrawerContent>
      </Drawer>

      <FeedbackModal open={feedbackOpen} onOpenChange={setFeedbackOpen} defaultType={feedbackType} />
    </>
  );
}
